import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, ClickAwayListener, makeStyles } from '@material-ui/core';
import ArrowTooltip from '../arrowTooltip';
import Scrollbars from '../scrollbars';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  tooltipPopper: {
    cursor: 'default',
    pointerEvents: 'auto',
    minWidth: 308,
  },
  tooltipWrapper: {
    minWidth: 308,
    padding: spacing(6),
  },
  tooltipTitle: {
    color: `${primary.bluish2}`,
    wordBreak: 'break-word',
    textAlign: 'left',
    marginBottom: spacing(2),
  },
  scrollViewport: {
    maxHeight: 220,
  },
  scrollY: {
    right: -15,
  },
}));

const ScrollableTooltip = ({
  tooltipPopperClass = undefined,
  tooltipWrapperClass = undefined,
  tooltipContentWrapperClass = undefined,
  scrollViewportClass = undefined,
  title = '',
  contentData,
  children,
  shouldDisablePortal = true,
  isTooltipDisabled = false,
  isTooltipArrowHidden = false,
  renderContent,
  ...rest
}) => {
  const classes = useStyles();

  const [isOpened, setIsOpened] = useState(false);

  const onTooltipContentClick = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleCloseTooltip = () => {
    setIsOpened(false);
  };

  const handleOpenTooltip = async e => {
    if (!isOpened) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpened(true);
    } else {
      handleCloseTooltip();
    }
  };

  const renderTooltipContent = () => (
    <div>
      {title && (
        <Typography variant="h5" className={classes.tooltipTitle}>
          {`${title}:`}
        </Typography>
      )}
      <div className={classNames(tooltipContentWrapperClass)}>
        <Scrollbars
          verticalScrollClass={classes.scrollY}
          viewportClass={classNames(
            classes.scrollViewport,
            scrollViewportClass
          )}
        >
          {renderContent(contentData)}
        </Scrollbars>
      </div>
    </div>
  );

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      onClickAway={handleCloseTooltip}
    >
      <div onClick={onTooltipContentClick}>
        <ArrowTooltip
          open={isOpened}
          tooltipPopperClassName={classNames(
            classes.tooltipPopper,
            tooltipPopperClass
          )}
          tooltipClassName={classNames(
            classes.tooltipWrapper,
            tooltipWrapperClass
          )}
          tooltipLabel={renderTooltipContent()}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          shouldDisablePortal={shouldDisablePortal}
          interactive
          isArrowHidden={isTooltipArrowHidden}
          {...rest}
        >
          <div
            className={classNames(classes.childrenContainer, {
              [classes.childrenContainerDisabled]: isTooltipDisabled,
            })}
            onClick={!isTooltipDisabled ? handleOpenTooltip : () => {}}
          >
            {children}
          </div>
        </ArrowTooltip>
      </div>
    </ClickAwayListener>
  );
};

ScrollableTooltip.propTypes = {
  tooltipPopperClass: PropTypes.string,
  tooltipWrapperClass: PropTypes.string,
  tooltipContentWrapperClass: PropTypes.string,
  scrollViewportClass: PropTypes.string,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  shouldDisablePortal: PropTypes.bool,
  isTooltipDisabled: PropTypes.bool,
  isTooltipArrowHidden: PropTypes.bool,
  renderContent: PropTypes.func.isRequired,
};

export default ScrollableTooltip;
